<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading">
        <filter-managers
          :default-search-params="searchParams"
          :is-component-loading="isRequestingNow"
          @filtering="onFilter"
        ></filter-managers>
        <table-paginated-managers
          :is-component-loading="isRequestingNow"
          :paginated-object="paginatedObject"
          @edit-item="onPaginatedItemEdit"
          @delete-item="onPaginatedItemDelete"
        ></table-paginated-managers>
        <pagination-listing
          :pagination-data="paginationData"
          @go-prev="changePage('-')"
          @go-next="changePage('+')"
        ></pagination-listing>
      </v-flex>
      <modal-loading :is-show="isTableLoading"></modal-loading>
      <!-- Edit Modal -->
      <div v-if="account.role === MANAGER_TOP || account.role === MANAGER_SERVICE">
        <edit-modal-manager
          :item="currentEditableItem"
          :is-show-dialog="isShowUserDialog"
          :is-modal-loading="isLoadingUserDialog"
          :action-label="userDialogActionLabel"
          :errors="errorsUserDialog"
          @on-close="onCloseUserDialog"
          @on-action="onActionUserDialog"
        />
        <v-btn
          color="pink"
          dark
          fixed
          bottom
          right
          fab
          @click="currentEditableItem = {}; isShowUserDialog = true"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>
      <!-- End: Edit Modal -->
      <accept-dialog
      :is-open-dialog="isShowDeleteDialog"
      @on-close="isShowDeleteDialog=false"
      @on-accept="onAcceptDeletion"
      >
       <div class="pt-6">
         {{acceptModalText}}
       </div>
      </accept-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
/* constants */
import { PER_PAGE } from '../../constants/listing';
import { MANAGER_TOP, MANAGER_SERVICE } from '../../constants/roles';
/* utils */
import { deepMerge, prepareValidationMessages, strReplaceAll } from '../../utils/index';
/* proxies */
import ManagerProxy from '../../proxies/Manager/ManagerProxy';
/* mixins */
import defaultListingMixin from '../../mixins/listing/defaultListingMixin';
import updatePageContentMixin from '../../mixins/updatePageContentMixin';
import paginationMixin from '../../mixins/listing/paginationMixin';
/* components */
import TablePaginatedManagers from '../../components/listings/TablePaginatedManagers.vue';
import ModalLoading from '../../components/loaders/ModalLoading.vue';
import FilterManagers from '../../components/listings/FilterManagers.vue';
import PaginationListing from '../../components/listings/PaginationListing.vue';
import EditModalManager from '../../components/listings/EditModalManager.vue';
import AcceptDialog from '../../components/AcceptDialog.vue';

export default {
  name: 'ManagersIndex',
  mixins: [
    defaultListingMixin,
    updatePageContentMixin,
    paginationMixin,
  ],
  components: {
    ModalLoading,
    TablePaginatedManagers,
    FilterManagers,
    PaginationListing,
    EditModalManager,
    AcceptDialog,
  },
  data() {
    return {
      /* Page management */
      pageLoading: false,
      MANAGER_TOP,
      MANAGER_SERVICE,
      /* Create - Edit user */
      isLoadingUserDialog: false,
      errorsUserDialog: {},
      isShowUserDialog: false,
      currentEditableItem: {},
      /* Delete user */
      isShowDeleteDialog: false,
      /* Listing management */
      isTableLoading: false,
      searchParams: {
        search: '',
        active: 1,
        role: null,
        page: {
          number: 1,
          size: PER_PAGE,
        },
      },
    };
  },
  created() {
    this.isTableLoading = true;
    this.fetchData();
  },
  computed: {
    /* edit modal */
    userDialogActionLabel() {
      if (this.isShowUserDialog) {
        return (this.currentEditableItem.name)
          ? this.lang(this.$store, 'sap/listings.global.edit.save')
          : this.lang(this.$store, 'sap/listings.global.edit.create');
      }
      return '';
    },
    /* accept modal */
    acceptModalText() {
      return strReplaceAll(this.lang(this.$store, 'sap/listings.global.delete.question'), {
        user: this.currentEditableItem.name,
      });
    },
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
  },
  methods: {
    fetchData() {
      this.isRequestingNow = true;
      new ManagerProxy()
        .setParameters(this.searchParams)
        .all()
        .then((response) => {
          this.paginatedObject = response.data.data.managers;
        })
        .catch((error) => {
          console.warn(error);
          this.paginatedObject = {};
        })
        .finally(() => {
          this.isTableLoading = false;
          this.isRequestingNow = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    onPaginatedItemEdit(item) {
      this.currentEditableItem = item;
      this.isShowUserDialog = true;
    },
    onPaginatedItemDelete(item) {
      this.currentEditableItem = item;
      this.isShowDeleteDialog = true;
    },
    onFilter(event) {
      const newSearchParams = Object.assign({}, event);
      if (_.isString(newSearchParams.role)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.role = [newSearchParams.role];
      }
      this.searchParams = deepMerge(this.searchParams, deepMerge(newSearchParams, {
        page: {
          number: 1,
        },
      }));
    },
    onCloseUserDialog() {
      this.isShowUserDialog = false;
    },
    onActionUserDialog(item) {
      if (_.isObject(item)) {
        this.isLoadingUserDialog = true;
        let client = new ManagerProxy();
        if (this.currentEditableItem.uuid) {
          client = client.update(this.currentEditableItem.uuid, item);
        } else {
          client = client.create(item);
        }

        client.then((response) => {
          this.currentEditableItem = response.data.data.manager;
          this.$store.dispatch('page/updateCurrentPageContent');
          this.isShowUserDialog = false;
        })
          .catch((response) => {
            this.errorsUserDialog = prepareValidationMessages(response.data.data);
          })
          .finally(() => {
            this.isLoadingUserDialog = false;
          });
      }
    },
    onAcceptDeletion() {
      this.isRequestingNow = true;
      new ManagerProxy().destroy(this.currentEditableItem.uuid)
        .catch((response) => {
          console.error(response);
          alert(`Error: ${response.status}`);
          this.isRequestingNow = false;
        })
        .then(() => {
          this.fetchData();
        });
    },
  },
};
</script>
